import * as React from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { ADD_PANGOLIN_PAGE_TYPES, HistoryService } from 'services/base/history';
import { BasePath } from 'services/base/page_path';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { FormSubmitEvent } from 'ui/base/types';
import { BackButton } from 'ui/base/button/back_button';
import { ActiveButton } from 'ui/public/button';
import { TextAreaInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { AddPangolinPage } from 'ui/add_pangolin/add_pangolin_page/add_pangolin_page';
import styles from './add_pangolin_mnemonic.module.css';
import Messages from './add_pangolin_mnemonic_messages';
import { AddPangolinMnemonicStore } from './add_pangolin_mnemonic_store';

type BaseAddPangolinMnemonicProps = {
  store: AddPangolinMnemonicStore;
};

export const BaseAddPangolinMnemonic = observer(({ store }: BaseAddPangolinMnemonicProps) => {
  const accounts = useAccountsDomain();
  const history = useHistory();
  const handleSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSubmit(accounts));
    if (result.kind === ResultKind.OK) {
      // Clear all the pangolin pages
      const cleared = HistoryService.clearWhile(history, ADD_PANGOLIN_PAGE_TYPES);
      if (!cleared) {
        // If any of them remain on the stack, it means they were the only things there
        // so we must replace the last remaining one with the Home page
        await HistoryService.push(history, { kind: BasePath.HOME, pangolinID: result.pangolin.id });
        await HistoryService.push(history, { kind: BasePath.ADD_PANGOLIN_RENAME, pangolinID: result.pangolin.id });
      } else {
        await HistoryService.push(history, { kind: BasePath.ADD_PANGOLIN_RENAME, pangolinID: result.pangolin.id });
      }
    }
  }, [store, accounts, history]);

  return (
    <AddPangolinPage>
      <BackButton/>
      <form onSubmit={handleSubmit}>
        <div className={styles.instructions}>
          {Messages.instructions()}
        </div>
        <div className={styles.textarea}>
          <TextAreaInput
            store={store.mnemonic}
            placeholder={Messages.placeholder()}
          />
        </div>
        <ErrorMessage message={store.mnemonicError}/>
        <div className={styles.action}>
          <ActiveButton busy={store.busy} type='submit'>
            {Messages.submit()}
          </ActiveButton>
        </div>
      </form>
    </AddPangolinPage>
  );
});

export const AddPangolinMnemonic = () => {
  const [store] = React.useState(new AddPangolinMnemonicStore());
  return <BaseAddPangolinMnemonic store={store}/>;
};
